import {
  type CreditCard,
  PaymentVerifyResultEnum,
  ResponseEnum,
} from "@b2bportal/card-api";
import { trackCheckoutEvent } from "@checkout/tracking/sink";
import { verifyPaymentMethod, listPaymentMethods } from "@hopper-b2b/api";
import { ADD_PAYMENT } from "@hopper-b2b/types";

import { CardPaymentSelectors } from "..";
import type { VerifyPaymentMethodEvent } from "../events";
import { mergeVerifiedPaymentMethod } from "../mergeVerifiedPaymentMethod";
import {
  type ParentContextWithCardPayment,
  type PaymentError,
  PaymentErrorModalTypes,
} from "../types";

export const verifyPaymentService = (
  context: ParentContextWithCardPayment,
  event: VerifyPaymentMethodEvent
) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise<VerifyPaymentResponse>(async (resolve, reject) => {
    const errorCase = (error) => {
      trackCheckoutEvent({
        eventName: ADD_PAYMENT,
        properties: { success: false },
      });
      reject({
        type: PaymentErrorModalTypes.VerifyPayment,
        data: error,
      });
    };
    try {
      const verifyRes = await verifyPaymentMethod({
        token: event.token,
        usageType: event.usageType,
      });
      switch (verifyRes.Response) {
        case ResponseEnum.Success: {
          switch (verifyRes.value.PaymentVerifyResult) {
            case PaymentVerifyResultEnum.Success: {
              const listReqBody = CardPaymentSelectors.getPaymentListRequest({
                context,
              });
              const listRes = await listPaymentMethods(listReqBody);
              switch (listRes.Response) {
                case ResponseEnum.Success: {
                  trackCheckoutEvent({
                    eventName: ADD_PAYMENT,
                    properties: { success: true },
                  });
                  const verifiedPaymentMethod = verifyRes.value.payment;
                  resolve({
                    // The reloaded list may not contain the card just
                    // verified (single-use cards can be hidden from card
                    // lists), so merge it back in.
                    paymentMethods: mergeVerifiedPaymentMethod(
                      listRes.value,
                      verifiedPaymentMethod
                    ),
                    selectedPaymentMethodId: verifiedPaymentMethod.id,
                    verifiedPaymentMethod,
                  });
                  break;
                }
                case ResponseEnum.Failure: {
                  reject({
                    type: PaymentErrorModalTypes.ListPaymentMethods,
                    data: listRes.errors,
                  });
                  break;
                }
              }
              break;
            }
            default: {
              errorCase(verifyRes.value.PaymentVerifyResult);
            }
          }
          break;
        }
        case ResponseEnum.Failure: {
          errorCase(verifyRes.errors);
          break;
        }
      }
    } catch (e) {
      errorCase(e);
    }
  });

export interface SuccessfulVerifyPaymentResponse {
  paymentMethods: CreditCard[];
  selectedPaymentMethodId: string;
  /** The card returned by verify. */
  verifiedPaymentMethod?: CreditCard;
}

export type VerifyPaymentResponse =
  | SuccessfulVerifyPaymentResponse
  | PaymentError;
